import type { EventGuests } from '@cocoonspace/sdk-js/types/eventGuests'
import type { EventParticipant } from '@cocoonspace/sdk-js/types/eventParticipant'
import { useListTopGuests } from '@cocoonspace/shared/hooks/cocoon-api'
import { useTranslation } from 'next-i18next'
import {
	type DetailedHTMLProps,
	type InputHTMLAttributes,
	type PropsWithChildren,
	forwardRef,
	useMemo,
	useState,
} from 'react'

import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '~/components/ui/popover'
import { GuestAutocompleteOption } from './guest-autocomplete-option'

interface InputTopGuestsProps
	extends DetailedHTMLProps<
		InputHTMLAttributes<HTMLInputElement>,
		HTMLInputElement
	> {
	onAddEmail?: (guest: EventParticipant) => void
	guests: EventGuests
	value: string
}

export const InputTopGuests = forwardRef<
	HTMLInputElement,
	PropsWithChildren<InputTopGuestsProps>
>(({ onAddEmail, ...props }, ref) => {
	const { t } = useTranslation()
	const { data } = useListTopGuests({ limit: 10 })
	const [isOpen, setIsOpen] = useState(false)

	const filteredGuests = useMemo(
		() =>
			data?.data.filter(
				({ email }) =>
					email.includes(props.value) &&
					!props.guests.some((guest) => guest.email === email),
			),
		[data?.data, props.guests, props.value],
	)

	const handleAddTopUserEmail = (guest: EventParticipant) => {
		onAddEmail?.(guest)
	}

	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			modal={true}
		>
			<PopoverTrigger asChild>
				<div>
					<input
						ref={ref}
						{...props}
						className='w-full p-1 placeholder:text-black placeholder:italic focus:outline-none'
						placeholder={t('eventDetails:share.enterEmailGuests') as string}
						data-testid='top-users-input'
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(true)
						}}
						onFocus={() => setIsOpen(true)}
					/>
				</div>
			</PopoverTrigger>

			{(filteredGuests?.length || 0) > 0 && props.value.length >= 3 && (
				<PopoverContent
					className='p-0'
					side='bottom'
					align='start'
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<ul
						data-testid='top-users-list'
						className='text-xs'
					>
						{filteredGuests?.map((guest) => (
							<li key={guest.email}>
								<GuestAutocompleteOption
									guest={guest}
									onClick={handleAddTopUserEmail}
								/>
							</li>
						))}
					</ul>
				</PopoverContent>
			)}
		</Popover>
	)
})

InputTopGuests.displayName = 'InputTopGuests'
